// vg-binding: P7.D-12
// vg-binding: P7.D-159
//
// apps/vendor/src/features/products/detail/ApprovalHistoryTimeline.tsx
//
// Phase 7 Amendment #3 Task 83 — vertical timeline of vendor product
// approval events. Uses the existing /products/:id/approval-history
// endpoint via useApprovalHistory hook.

import { useState } from 'react';

import { useTranslation } from '../../../lib/i18n/index.js';
import { useApprovalHistory } from '../api.js';

interface Props {
  product_id: string;
  total_events: number;
}

const INITIAL_LIMIT = 20;
const PAGE_INCREMENT = 20;

function formatTimestamp(ms: number, locale: string): string {
  const d = new Date(ms);
  return d.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ApprovalHistoryTimeline({
  product_id,
  total_events,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation('products');
  const [limit, setLimit] = useState(INITIAL_LIMIT);
  const query = useApprovalHistory(product_id);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2 py-4">
        {Array.from({ length: 3 }).map((_v, i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-md border border-pw-border bg-pw-surface"
          />
        ))}
      </div>
    );
  }

  const rows = query.data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
        {t('detail.timeline.empty')}
      </p>
    );
  }

  const visible = rows.slice(0, limit);
  const hasMore = rows.length > visible.length;

  return (
    <div
      data-testid="approval-history-timeline"
      className="flex flex-col gap-3"
    >
      <ol className="relative flex flex-col gap-4 border-l border-pw-border pl-4">
        {visible.map((row, idx) => {
          const label =
            t(`detail.timeline.eventType.${row.event}`, { defaultValue: row.event });
          return (
            <li
              key={`${row.occurred_at}-${idx}`}
              data-testid={`timeline-event-${row.event}`}
              className="relative"
            >
              <span
                aria-hidden="true"
                className="absolute -left-[19px] top-1.5 h-3 w-3 rounded-full border-2 border-pw-bg bg-pw-primary"
              />
              <div className="flex flex-col gap-1 rounded-md border border-pw-border bg-pw-surface px-3 py-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-pw-text">{label}</span>
                  <span className="font-mono text-[11px] text-pw-text-muted">
                    {formatTimestamp(row.occurred_at, i18n.language)}
                  </span>
                </div>
                <p className="text-xs text-pw-text-muted">
                  {t('detail.timeline.by')} <span className="font-mono">{row.actor}</span>
                </p>
                {row.reason ? (
                  <p className="text-xs italic text-pw-text">{row.reason}</p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      <div className="flex items-center justify-between gap-2 text-xs text-pw-text-muted">
        <span>
          {t('detail.timeline.showingCount', {
            shown: visible.length,
            total: Math.max(total_events, rows.length),
          })}
        </span>
        {hasMore ? (
          <button
            type="button"
            data-testid="approval-history-load-more"
            onClick={() => setLimit((n) => n + PAGE_INCREMENT)}
            className="rounded-md border border-pw-border bg-pw-surface px-3 py-1 text-xs font-medium text-pw-text hover:bg-pw-bg"
          >
            {t('detail.timeline.loadMore')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default ApprovalHistoryTimeline;
